import { z } from 'zod';

import {
  DateTimeSchema,
  objectIdRef,
  ObjectIdSchema,
  ObjectIdString,
  subDocument,
} from '../common.js';
import { AccountDTO } from './accountSchema.js';
import {
  ActivityDataMappingConfig,
  ChromeExtensionConfig,
  DataIQConfig,
  EntityFields,
  TalentIQConfig,
} from './configSchemas.js';
import { StaffIQConfig } from './staffIqConfigSchema.js';
import { RelatedEntityMetadataMap } from '../../interfaces.js';
import { ToroEnvironment } from '../../config.js';

// ------------------------------------------------------------------------------------------------------------------ //
//region IntegrationConfig schemas

export const SsnRedactionMode = z.enum(['redact', 'keepLast4', 'preserve']);
export type SsnRedactionMode = z.infer<typeof SsnRedactionMode>;

export const RedactionConfig = z.object({
  ssnRedaction: SsnRedactionMode.optional(),
});
export type RedactionConfig = z.infer<typeof RedactionConfig>;

export const NlpConfig = z.object({
  spanishFalsePositiveFiltering: z.boolean().default(false),
});
export type NlpConfig = z.infer<typeof NlpConfig>;

export const AnalyticsConfig = z.object({
  enabled: z.boolean().nullish(),
});
export type AnalyticsConfig = z.infer<typeof AnalyticsConfig>;

export const IntegrationConfig = z.object({
  preloadNotes: z.boolean().default(true),
  preloadInsights: z.boolean().default(false),
  preloadJobs: z.boolean().default(true),
  redactionConfig: RedactionConfig,
  nlpConfig: NlpConfig,
  talentIqConfig: TalentIQConfig,
  dataIqConfig: DataIQConfig,
  activityDataMappings: ActivityDataMappingConfig.array(),
  entityFields: EntityFields,
  staffIqConfig: StaffIQConfig,
  analyticsConfig: AnalyticsConfig.nullish(),
  chromeExtensionConfig: ChromeExtensionConfig.optional(),
});
export type IntegrationConfig = z.infer<typeof IntegrationConfig>;

export const SimpleConfig = IntegrationConfig.pick({
  preloadNotes: true,
  preloadInsights: true,
  preloadJobs: true,
});
export type SimpleConfig = z.infer<typeof SimpleConfig>;

// SimpleConfig with all defaults removed
export const SimpleConfigUpdate = SimpleConfig.required().partial();
export type SimpleConfigUpdate = z.infer<typeof SimpleConfigUpdate>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Base Integration types
export const AtsType = z.enum(['bullhorn', 'fakeAts']);
export type AtsType = z.infer<typeof AtsType>;

export const NonAtsIntegrationType = z.enum(['textUs']);
export type NonAtsIntegrationType = z.infer<typeof NonAtsIntegrationType>;

export const IntegrationType = z.enum([...AtsType.options, ...NonAtsIntegrationType.options]);
export type IntegrationType = z.infer<typeof IntegrationType>;

export const IntegrationStatus = z.enum(['active', 'disabled', 'terminated', 'deleted']);
export type IntegrationStatus = z.infer<typeof IntegrationStatus>;

export const SecretMetadata = z.object({
  secretName: z.string(),
  version: z.string(),
  schemaName: z.string(),
  schemaVersion: z.number().int(),
  tags: z.record(z.string(), z.string()),
  updatedAt: DateTimeSchema,
});
export type SecretMetadata = z.infer<typeof SecretMetadata>;

export const IIntegration = z.object({
  _id: ObjectIdSchema,
  name: z.string().optional(),
  account: objectIdRef('Account'),
  status: IntegrationStatus.default('active'),
  type: IntegrationType,
  apiData: z.record(z.string(), z.unknown()),
  config: IntegrationConfig.partial().nullish(),
  secretMetadata: subDocument(SecretMetadata).nullish(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IIntegration = z.infer<typeof IIntegration>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Bullhorn Integrations

export const BullhornSessionData = z.object({
  redirectUri: z.string().nullish(),
  oauthUrl: z.string().nullish(),
  userId: z.string().nullish(),
  restServices: z.string().nullish(),
  restUrl: z.string().nullish(),
  bhRestToken: z.string().nullish(),
  refreshToken: z.string().nullish(),
});
export type BullhornSessionData = z.infer<typeof BullhornSessionData>;

export const BullhornIntegrationApiData = z.object({
  enableBullhornInternalApi: z.boolean().default(false),
  authUrl: z.string().nullish().default(''),
  restUrl: z.string().nullish().default(''),
  redirectUri: z.string().nullish().default('https://toroinsights.ai'),
  swimlane: z.string().nullish().default(''),
  corpId: z.string().nullish().default(''),
  sessionData: BullhornSessionData.default({}),
  internalSessionData: BullhornSessionData.default({}),
  entityFieldExclusions: z.record(z.string(), z.string().array()).default({}),
});
export type BullhornIntegrationApiData = z.infer<typeof BullhornIntegrationApiData>;

export const BullhornIntegrationApiDataInput = BullhornIntegrationApiData.required().partial();

export const IBullhornIntegration = z.object({
  ...IIntegration.shape,
  type: z.literal('bullhorn'),
  apiData: BullhornIntegrationApiData,
  config: IntegrationConfig,
  metadata: z.object({
    relatedEntities: RelatedEntityMetadataMap,
  }),
});
export type IBullhornIntegration = z.infer<typeof IBullhornIntegration>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region DTOs

export const IntegrationDTO = z.object({
  _id: ObjectIdString,
  account: AccountDTO.or(ObjectIdString),
  status: IntegrationStatus,
  type: IntegrationType,
  name: z.string().optional(),
  corpId: z.string().nullish(),
  apiUserId: z.string().nullish(),
  // Exclude activityDataMappings from the general DTO because they're huge and prone to errors if misconfigured
  config: IntegrationConfig.omit({ activityDataMappings: true }).partial().nullish(),
  createdAt: DateTimeSchema.optional(),
  updatedAt: DateTimeSchema.optional(),
  metadata: z.object({ relatedEntities: RelatedEntityMetadataMap }).partial().optional(),
});
export type IntegrationDTO = z.infer<typeof IntegrationDTO>;

export const PopulatedIntegrationDTO = IntegrationDTO.safeExtend({ account: AccountDTO });
export type PopulatedIntegrationDTO = z.infer<typeof PopulatedIntegrationDTO>;

export const IntegrationCreateSchema = z.object({
  accountId: ObjectIdString,
  type: IntegrationType,
  status: IntegrationStatus.optional(),
  name: z.string().optional(),
});

/** @deprecated - should be removed when we delete the old integration management pages */
export const IntegrationUpdateSchema = z.object({
  status: IntegrationStatus.optional(),
  name: z.string().optional(),
  apiData: z.unknown().optional(),
  config: z.unknown().optional(),
});

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Integration secrets

export const BullhornSecretData = z.object({
  _schemaName: z.literal('bullhornIntegrationSecret').default('bullhornIntegrationSecret'),
  _schemaVersion: z.literal(1).default(1),
  clientId: z.string().default(''),
  clientSecret: z.string().default(''),
  username: z.string().default(''),
  password: z.string().default(''),
  internalUsername: z.string().default(''),
  internalPassword: z.string().default(''),
});
export type BullhornSecretData = z.infer<typeof BullhornSecretData>;

export const TextUsSecretData = z.object({
  _schemaName: z.literal('textUsIntegrationSecret').default('textUsIntegrationSecret'),
  _schemaVersion: z.literal(1).default(1),
  apiKey: z.string().default(''),
  webhookSecret: z.string().default(''),
});
export type TextUsSecretData = z.infer<typeof TextUsSecretData>;

// Validates any integration secret by its self describing _schemaName tag
export const IntegrationSecret = z.discriminatedUnion('_schemaName', [
  BullhornSecretData,
  TextUsSecretData,
]);
export type IntegrationSecretData = z.infer<typeof IntegrationSecret>;

export const BullhornSecretValues = BullhornSecretData.omit({
  _schemaName: true,
  _schemaVersion: true,
});
export const BullhornSecretValuesInput = BullhornSecretValues.required().partial();

/*
 * Admin-facing Bullhorn config as two objects: the non-secret apiData (from the doc) and the
 * secret credentials (from the vault). getConfig returns this; the form edits it and submits the
 * dirty fields of each object separately — apiData to the doc, credentials to the vault.
 */
export const BullhornApiConfig = z.object({
  apiData: BullhornIntegrationApiData,
  credentials: BullhornSecretValues,
});
export type BullhornApiConfig = z.infer<typeof BullhornApiConfig>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
